<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "FamilyDetail",
};
</script>
<script setup lang="ts">
import AppService from "@/api/app";
import ChildService, { type TChild } from "@/api/child";
import DealerService from "@/api/dealer";
import DeviceService from "@/api/device";
import FamilyService, { DefaultFamily, type TFamily } from "@/api/family";
import RoleService from "@/api/role";
import ServiceService from "@/api/service";
import UserService, { type TUser } from "@/api/user";
import ChildrenTable from "@/components/models/child/ChildTable.vue";
import UsersTable from "@/components/models/user/UsersTable.vue";
import { i18n } from "@/locales";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import { useSnackStore } from "@/stores/snack";
import { generateFormOptions, type PaginatedType, type VFormFieldChoice, type VFormOptions } from "@/utils/DjangoTypes";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";
import { useAbility } from "@casl/vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { type AxiosResponse } from "axios";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();

const currentFamilyId = Number(route.params.id);

definePage({
  meta: {
    title: "Detail of Family",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.DEALER);
    },
  },
});

const { t } = i18n.global;

const snackStore = useSnackStore();
const queryClient = useQueryClient();
const fetching = ref(true);

const entity = ref<Partial<TFamily>>(DefaultFamily);
const valid = ref(false);

const familiesChoices = ref<VFormFieldChoice<any>[]>([]);

const dealerChoices = ref<VFormFieldChoice<any>[]>([]);

const devicesChoices = ref<VFormFieldChoice<any>[]>([]);

const appInstancesChoices = ref<VFormFieldChoice<any>[]>([]);

const userOptions = ref<VFormOptions>({});
const usersChoices = ref<VFormFieldChoice<any>[]>([]);
const rolesChoices = ref<VFormFieldChoice<any>[]>([]);

const childOptions = ref<VFormOptions>({});
const childChoices = ref<VFormFieldChoice<any>[]>([]);

const serviceChoices = ref<VFormFieldChoice<any>[]>([]);

const { mutateAsync: save, isPending } = useMutation({
  mutationFn: async (family: Partial<TFamily>): Promise<AxiosResponse<TFamily> | any> => {
    if (family.id) {
      return await FamilyService.family.edit(family, family.id);
    } else {
      return await FamilyService.family.add(family);
    }
  },
  onSuccess: async (): Promise<void> => {
    router.push({ name: "/families" });
    await queryClient.invalidateQueries({ queryKey: ["families"] });
    snackStore.showSuccess(t("crud.success"));
  },
  onError: (error: any): void => {
    snackStore.showError(error);
  },
});

const queriesTableParams: Record<string, ServerTableParamsType> = {
  children: {
    ...DefaultServerTableParams,
    filter: {
      families: currentFamilyId,
    },
  },
  users: {
    ...DefaultServerTableParams,
    filter: {
      families: currentFamilyId,
    },
  },
};
provide("queriesTableParams", queriesTableParams);

const {
  data: children,
  isPending: isChildrenPending,
  isFetching: isChildrenFetching,
} = useQuery({
  queryKey: ["children"],
  queryFn: async (): Promise<PaginatedType<TChild[]> | undefined> => {
    try {
      const [result, devices, services, appInstances] = await Promise.all([
        ChildService.child.getAll(queriesTableParams.children),
        DeviceService.device.getAllSelectable(),
        ServiceService.service.getAllSelectable({ ...DefaultServerTableParams, itemsPerPage: 100 }),
        AppService.appInstance.getAllSelectable(),
      ]);

      devicesChoices.value = devices;
      serviceChoices.value = services;
      appInstancesChoices.value = appInstances;

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
});

const {
  data: users,
  isPending: isUsersPending,
  isFetching: isUsersFetching,
} = useQuery({
  queryKey: ["users"],
  queryFn: async (): Promise<PaginatedType<TUser[]> | undefined> => {
    try {
      const [result, dealersSelectable] = await Promise.all([
        UserService.user.getAll(queriesTableParams.users),
        DealerService.dealer.getAllSelectable(),
      ]);

      dealerChoices.value = dealersSelectable;

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
});

onMounted(async () => {
  fetching.value = false;

  const allRequests = Promise.all([
    FamilyService.family.get(currentFamilyId),
    FamilyService.family.getAllSelectable(),
    UserService.user.getOptions(),
    UserService.user.getAllSelectable(),
    RoleService.role.getAllSelectable(),
    ChildService.child.getOptions(),
    ChildService.child.getAllSelectable(),
  ]);

  const [family, familiesSelectable, userDRFOptions, usersSelectable, rolesSelectable, childDRFOptions, childrenSelectable] =
    await allRequests;

  entity.value = family.data;
  familiesChoices.value = familiesSelectable;

  usersChoices.value = usersSelectable;
  rolesChoices.value = rolesSelectable;
  userOptions.value = generateFormOptions(userDRFOptions.data.actions.POST);

  childChoices.value = childrenSelectable;
  childOptions.value = generateFormOptions(childDRFOptions.data.actions.POST);

  fetching.value = false;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <v-form v-model="valid">
        <v-card>
          <v-card-title class="tw:!px-6 tw:!py-8">
            <div class="tw:text-3xl">{{ t("models.family.typeof") }}</div>
          </v-card-title>
          <v-card-text class="tw:!px-6 tw:!pb-8">
            <v-row>
              <v-col cols="2">
                <v-text-field
                  v-model="entity.name"
                  :label="t('models.family.name')"
                  class="required"
                  :rules="[(value) => !!value]"
                />
              </v-col>
              <v-col>
                <FieldInputSelect
                  v-model="entity.members"
                  :label="t('models.family.members')"
                  multiple
                  :service="UserService.user"
                />
              </v-col>
              <v-col>
                <FieldInputSelect
                  v-model="entity.children"
                  :label="t('models.family.children')"
                  multiple
                  :service="ChildService.child"
                />
              </v-col>
            </v-row>
            <v-row justify="end">
              <v-col cols="auto">
                <v-btn
                  color="red"
                  variant="text"
                  @click="router.push({ name: '/families' })"
                >
                  <v-icon>mdi-close</v-icon>
                  {{ t("modals.closeWithoutSave") }}
                </v-btn>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="primary"
                  :disabled="!valid"
                  :loading="isPending"
                  @click="save(entity)"
                >
                  <v-icon>mdi-content-save</v-icon>
                  {{ t("modals.save") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-form>

      <div class="tw:!mt-10">
        <users-table
          :query-key="'users'"
          :data="users"
          :loading="isUsersPending || isUsersFetching"
          :families="familiesChoices"
          :dealers="dealerChoices"
          :roles="rolesChoices"
          :default-family="currentFamilyId"
          :options="userOptions"
          headline-text="models.family.usersTable"
          headline-class="tw:text-1xl tw:-mb-6"
        />
      </div>

      <div class="tw:!mt-10">
        <children-table
          :query-key="'children'"
          :data="children"
          :loading="isChildrenPending || isChildrenFetching"
          :devices="devicesChoices"
          :default-family="currentFamilyId"
          :options="childOptions"
          :without-anonymouse="true"
          headline-text="models.family.childrenTable"
          headline-class="tw:text-1xl tw:-mb-6"
        />
      </div>
    </div>
  </v-container>
</template>

<style scoped></style>
